import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_COLORIZE_STATE,
  buildColorizeRequest,
  hasSourceImage,
  type ColorizeFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const ColorizePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<ColorizeFormState>(DEFAULT_COLORIZE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.colorize);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to colorize.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildColorizeRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ColorLensIcon fontSize="small" />
        <Typography variant="subtitle2">Colorize</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image (black-and-white usually) to colorize.</Alert>
      )}

      <TextField
        label="Color style (optional)"
        value={state.colorStyle}
        onChange={e => setState(s => ({ ...s, colorStyle: e.target.value }))}
        placeholder="e.g., 1970s warm Kodachrome"
        size="small"
        fullWidth
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Saturation · {state.saturation.toFixed(2)}
        </Typography>
        <Slider
          value={state.saturation}
          onChange={(_, v) => setState(s => ({ ...s, saturation: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.enhanceFaces}
            onChange={e => setState(s => ({ ...s, enhanceFaces: e.target.checked }))}
            size="small"
          />
        }
        label="Enhance face colors"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Colorize failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <ColorLensIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Colorizing…' : 'Colorize'}
        </Button>
      </Box>
    </Stack>
  );
};
